import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Button, Group, Skeleton, Stack, Text} from '@mantine/core';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import EntityStore from 'stores/EntityStore';
import CreditUsageBar from 'tenants/admin/ai-usage/CreditUsageBar';
import {fetchUsageSummaryForUser, UserUsageSummaryResponse} from 'tenants/admin/ai-usage/MeteringDAO';
import {runUsageExportWithTrayTask} from 'tenants/admin/llm-features/UsageExportTrayTask';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const USAGE_HISTORY_MONTHS = 3;

const CURRENT_USAGE_DESCRIPTION =
  'Track how many credits you’ve used this billing period. Usage resets at the start of each period.';
const USAGE_HISTORY_DESCRIPTION = `Download a CSV of your usage from the past ${USAGE_HISTORY_MONTHS} months.`;

export default function UserUsageSummary(): JSX.Element {
  const [summary, setSummary] = useState<UserUsageSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);

    const fetchSummary = async (): Promise<void> => {
      try {
        const response = await fetchUsageSummaryForUser();
        setSummary(response);
        // eslint-disable-next-line benchlint/always-throw-from-catch -- surface fetch failures as an error state, not a throw
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary().catch(() => {});
  }, []);

  const exportUserId = EntityStore.currentUserId;

  const handleDownload = (): void => {
    if (exportUserId == null) {
      return;
    }
    runUsageExportWithTrayTask({
      from_timestamp: moment.utc().subtract(USAGE_HISTORY_MONTHS, 'months').toDate(),
      user_id: exportUserId,
    });
  };

  let currentUsage: JSX.Element;
  if (isLoading) {
    currentUsage = (
      <Stack
        spacing="sm"
        role="status"
        aria-label="Loading usage"
        {...getTestProps({component: 'UserUsageSummary', element: 'loading'})}
      >
        <Skeleton height={20} width="40%" />
        <Skeleton height={8} />
      </Stack>
    );
  } else if (hasError || summary == null) {
    currentUsage = (
      <Alert variant="error" {...getTestProps({component: 'UserUsageSummary', element: 'error'})}>
        Unable to load usage data. Please try again later.
      </Alert>
    );
  } else {
    currentUsage = (
      <CreditUsageBar
        creditsUsed={summary.usage}
        creditLimit={summary.effectiveLimit}
        periodStart={new Date(summary.periodStart)}
        resetDate={new Date(summary.resetDate)}
        isTenantOutOfCredits={summary.isTenantOutOfCredits}
      />
    );
  }

  return (
    <Stack spacing="lg" {...getTestProps({component: 'UserUsageSummary', element: 'root'})}>
      {summary?.isTenantOutOfCredits && (
        <Alert
          variant="warning"
          icon={<BenchlingIcon icon="warning" />}
          title="Your tenant is out of Benchling credits."
          {...getTestProps({component: 'UserUsageSummary', element: 'tenantOutOfCreditsBanner'})}
        >
          Contact your admin to add more credits.
        </Alert>
      )}

      <Stack spacing="xs">
        <Text fw="bold" size="lg">
          Current Usage
        </Text>
        <Text>{CURRENT_USAGE_DESCRIPTION}</Text>
      </Stack>

      {currentUsage}

      <Group position="apart" noWrap={true} spacing="xl">
        <Stack spacing="xs">
          <Text fw="bold" size="lg">
            Usage History
          </Text>
          <Text>{USAGE_HISTORY_DESCRIPTION}</Text>
        </Stack>
        <Button
          variant="secondary"
          leftIcon={<BenchlingIcon icon="arrow-down-to-bracket" />}
          onClick={handleDownload}
          disabled={exportUserId == null}
          {...getTestProps({component: 'UserUsageSummary', element: 'download'})}
        >
          Download
        </Button>
      </Group>
    </Stack>
  );
}
